export type ProductSwatch = {
  name: string;
  hex: string;
};

export type Product = {
  id: string;
  slug: string;
  name: string;
  subtitle: string;
  category:
    | "shirts"
    | "outerwear"
    | "trousers"
    | "knits"
    | "accessories"
    | "eyewear"
    | "footwear"
    | "tech"
    | "home";
  price: number;
  currency: "USD";
  sizes: string[];
  swatches: ProductSwatch[];
  story: string;
  details: string[];
  badge?: string;
  hero?: boolean;
  fabric: string;
  origin: string;
  art: {
    base: string;
    accent: string;
    pattern: "weave" | "wave" | "knit" | "satin" | "noise";
  };
  // Optional retailer/curation product photo. Lives under /products/ in the
  // public dir. When absent the card falls back to the ProductArt SVG so the
  // grid stays visually consistent while sourcing catches up.
  image?: string;
  imageCredit?: string;
};

export const products: Product[] = [
  {
    id: "os1-001",
    slug: "theodore-linen-shirt",
    name: "Theodore Linen Shirt",
    subtitle: "Vermilion / unstructured collar",
    category: "shirts",
    price: 148,
    currency: "USD",
    sizes: ["XS", "S", "M", "L", "XL"],
    swatches: [
      { name: "Vermilion", hex: "#c83323" },
      { name: "Oxblood", hex: "#4a0d0d" },
      { name: "Bone", hex: "#ece1d1" }
    ],
    story:
      "Soft hand, slow weave, made to sit under a wool coat or breathe alone on a balcony at dusk. The button placket is hand-finished so it never gapes when you write.",
    details: [
      "100% wet-spun European linen, 165 gsm",
      "Mother-of-pearl buttons, hand-set",
      "Boxy fit, dropped shoulder, 73 cm back length (M)",
      "Garment-washed, tumble-softened"
    ],
    fabric: "European linen, 165 gsm",
    origin: "Cut and sewn in Porto",
    badge: "Signature",
    hero: true,
    art: { base: "#c83323", accent: "#7a1a14", pattern: "weave" },
    image: "/products/theodore-linen-shirt.jpg",
    imageCredit: "Reference: Zegna red collarless linen shirt"
  },
  {
    id: "os1-002",
    slug: "catherine-oxblood-coat",
    name: "Catherine Wool Coat",
    subtitle: "Oxblood, single-breasted, lined in silk",
    category: "outerwear",
    price: 620,
    currency: "USD",
    sizes: ["XS", "S", "M", "L", "XL"],
    swatches: [
      { name: "Oxblood", hex: "#4a0d0d" },
      { name: "Burnt Sienna", hex: "#8a2e1b" }
    ],
    story:
      "A coat built for the kind of week that has long walks in it. Cocoon line, drop sleeves, and a silk lining that won't snag on a knit.",
    details: [
      "85% virgin wool, 15% cashmere — 720 gsm",
      "Hand-pad-stitched lapel, horn buttons",
      "Full silk twill lining, monogram seam",
      "Below-knee length, single rear vent"
    ],
    fabric: "Wool-cashmere melton",
    origin: "Tailored in Biella",
    badge: "Limited 80 pieces",
    art: { base: "#4a0d0d", accent: "#2a0606", pattern: "satin" },
    image: "/products/catherine-oxblood-coat.jpg",
    imageCredit: "Reference: A.P.C. Sutherland checked jacket"
  },
  {
    id: "os1-003",
    slug: "samantha-crew-knit",
    name: "Samantha Crew Knit",
    subtitle: "Coral merino, jersey-loose neck",
    category: "knits",
    price: 230,
    currency: "USD",
    sizes: ["XS", "S", "M", "L"],
    swatches: [
      { name: "Coral", hex: "#ff8a72" },
      { name: "Rose", hex: "#f5b4a3" },
      { name: "Ember", hex: "#c4382c" }
    ],
    story:
      "Cropped on the body, long enough at the cuff to fold back twice. The neck is loose so you can wear it over a collared shirt or against the skin.",
    details: [
      "100% Italian merino wool, 12 gauge",
      "Fully-fashioned shoulders, no shoulder seam",
      "Drop tail hem, ribbed cuff",
      "Hand-finished neckline"
    ],
    fabric: "Italian merino, 12 gauge",
    origin: "Knit in Tuscany",
    art: { base: "#ff8a72", accent: "#c4382c", pattern: "knit" }
  },
  {
    id: "os1-004",
    slug: "operator-high-waist-trouser",
    name: "Operator High-Waist Trouser",
    subtitle: "Warm bone, pleated, hand-cuffed",
    category: "trousers",
    price: 285,
    currency: "USD",
    sizes: ["28", "30", "32", "34", "36"],
    swatches: [
      { name: "Warm Bone", hex: "#ece1d1" },
      { name: "Charcoal", hex: "#3b3a36" }
    ],
    story:
      "Built around the waist, not the hip. Double inward pleat, no belt loops — the trouser holds itself.",
    details: [
      "100% Australian wool, 280 gsm",
      "Side adjusters, button-fly",
      "Lined to the knee, hand-finished cuff",
      "Two slanted pockets, two rear jets"
    ],
    fabric: "Tropical wool",
    origin: "Cut in Naples",
    badge: "Tailored",
    art: { base: "#ece1d1", accent: "#a08c75", pattern: "weave" }
  },
  {
    id: "os1-005",
    slug: "os1-operator-tee",
    name: "OS1 Operator Tee",
    subtitle: "Heavy cotton, brick red",
    category: "shirts",
    price: 68,
    currency: "USD",
    sizes: ["XS", "S", "M", "L", "XL", "XXL"],
    swatches: [
      { name: "Brick", hex: "#a52f23" },
      { name: "Ash", hex: "#c9bdb0" },
      { name: "Black Cherry", hex: "#2a0808" }
    ],
    story:
      "The everyday shirt of the catalog. Heavy, opaque, slightly boxy. The neckband is rib-knit and finished so it won't curl on the third wash.",
    details: [
      "250 gsm long-staple cotton, ringspun",
      "Boxy fit, dropped shoulder",
      "Rib-knit neck, twin-needle hem",
      "Discreet woven label on the placket"
    ],
    fabric: "Heavy cotton jersey",
    origin: "Cut in Porto",
    art: { base: "#a52f23", accent: "#6a1a13", pattern: "noise" },
    image: "/products/os1-operator-tee.jpg",
    imageCredit: "Reference: Uniqlo mandarin collar shirt"
  },
  {
    id: "os1-006",
    slug: "letter-writer-cardigan",
    name: "Letter Writer Cardigan",
    subtitle: "Terracotta, shawl collar",
    category: "knits",
    price: 310,
    currency: "USD",
    sizes: ["S", "M", "L", "XL"],
    swatches: [
      { name: "Terracotta", hex: "#b04a2c" },
      { name: "Driftwood", hex: "#a48064" }
    ],
    story:
      "A long cardigan that gives you somewhere to put your hands at four in the afternoon. The shawl collar holds shape because it's pad-stitched, not just folded.",
    details: [
      "70% lambswool, 30% alpaca",
      "Shawl collar, leather-bound buttons",
      "Patch pockets at the hip",
      "Mid-thigh length"
    ],
    fabric: "Lambswool / alpaca",
    origin: "Knit in Scotland",
    art: { base: "#b04a2c", accent: "#5a1f0e", pattern: "knit" },
    image: "/products/letter-writer-cardigan.jpg",
    imageCredit: "Reference: ASOS Harrington bordeaux jacket"
  },
  {
    id: "os1-007",
    slug: "earpiece-leather-lanyard",
    name: "Earpiece Lanyard",
    subtitle: "Saddle leather, brushed brass",
    category: "accessories",
    price: 42,
    currency: "USD",
    sizes: ["One size"],
    swatches: [
      { name: "Chestnut", hex: "#6b3a22" },
      { name: "Black", hex: "#1a1a1a" }
    ],
    story:
      "Holds the OS1 earpiece — or any wired pair — against your collar so they don't slip when you walk. Pull-twist clasp.",
    details: [
      "Vegetable-tanned saddle leather",
      "Brushed brass hardware",
      "Adjustable length, 38–52 cm",
      "Engraving available on request"
    ],
    fabric: "Saddle leather",
    origin: "Tanned in Tuscany",
    art: { base: "#6b3a22", accent: "#2c160a", pattern: "satin" }
  },
  {
    id: "os1-008",
    slug: "samantha-silk-square",
    name: "Samantha Silk Square",
    subtitle: "Hand-rolled, signal red",
    category: "accessories",
    price: 58,
    currency: "USD",
    sizes: ["45×45 cm"],
    swatches: [
      { name: "Signal", hex: "#ff4632" },
      { name: "Coral", hex: "#ff8a72" },
      { name: "Bone", hex: "#ece1d1" }
    ],
    story:
      "Pocket square or wrist tie. Hand-rolled edge so it falls right. Printed with a pattern inspired by an OS waveform.",
    details: [
      "100% silk twill, 14 mm",
      "Hand-rolled edges",
      "Limited print run",
      "Comes in a recycled-card sleeve"
    ],
    fabric: "Silk twill",
    origin: "Printed in Como",
    art: { base: "#ff4632", accent: "#7a1a14", pattern: "wave" }
  },
  {
    id: "os1-009",
    slug: "begley-acetate-frame",
    name: "Begley Acetate Frame",
    subtitle: "Whiskey tortoise, key-hole bridge",
    category: "eyewear",
    price: 295,
    currency: "USD",
    sizes: ["49–20", "51–20", "53–20"],
    swatches: [
      { name: "Whiskey Tortoise", hex: "#7a4a1c" },
      { name: "Oxblood Tortoise", hex: "#3a1410" },
      { name: "Black Burl", hex: "#0e0808" }
    ],
    story:
      "A large keyhole square cut from a single sheet of Italian acetate. Worn at the desk, on the subway, against a sunset — the only frame in the catalog that works in all three lights.",
    details: [
      "8 mm Mazzucchelli acetate, hand-polished",
      "Custom seven-barrel hinge, no spring",
      "Key-hole bridge, etched temple tip",
      "Demo lenses; ship with prescription on request"
    ],
    fabric: "Italian acetate",
    origin: "Cut in Sabae, Japan",
    badge: "Signature",
    art: { base: "#7a4a1c", accent: "#2a1408", pattern: "satin" },
    imageCredit:
      "Reference: Warby Parker Begley in Whiskey Tortoise — https://medias.spotern.com/products/w310/0/419-1569509516.jpg"
  },
  {
    id: "os1-010",
    slug: "franklin-acetate-frame",
    name: "Franklin Acetate Frame",
    subtitle: "Ember tortoise, slimmer cut",
    category: "eyewear",
    price: 385,
    currency: "USD",
    sizes: ["47–22", "49–22", "51–22"],
    swatches: [
      { name: "Ember", hex: "#8a3a1c" },
      { name: "Walnut Burl", hex: "#4a2410" },
      { name: "Smoke", hex: "#2a2218" }
    ],
    story:
      "Smaller than the Begley, sat a hair higher on the bridge. The pair he wore on the call that didn't go through.",
    details: [
      "7 mm hand-laid acetate, rounded square",
      "Riveted core wire, anti-warp",
      "Saddle bridge, paddle temple",
      "Hand-finished tortoise pattern, no two alike"
    ],
    fabric: "Hand-laid acetate",
    origin: "Tokyo / Sabae",
    art: { base: "#8a3a1c", accent: "#2a1408", pattern: "satin" },
    imageCredit:
      "Reference: Clayton Franklin Model 756 (alternate attribution) — https://www.claytonfranklineyewear.com/"
  },
  {
    id: "os1-011",
    slug: "twombly-suede-wingtip",
    name: "Twombly Suede Wingtip",
    subtitle: "Milkshake suede, white running sole",
    category: "footwear",
    price: 360,
    currency: "USD",
    sizes: ["39", "40", "41", "42", "43", "44", "45"],
    swatches: [
      { name: "Milkshake", hex: "#d6c2a8" },
      { name: "Sand", hex: "#b89a78" },
      { name: "Walnut", hex: "#6b4a2c" }
    ],
    story:
      "A brogue upper on a foam running sole. Dress shoe from the knee up, sneaker from the ankle down — the compromise that defines a long workday.",
    details: [
      "Calf-suede upper, hand-perforated wing",
      "Full-grain leather lining, cork footbed",
      "EVA midsole, vulcanized rubber outsole",
      "Waxed cotton laces, two pairs included"
    ],
    fabric: "Italian calf suede",
    origin: "Lasted in Portugal",
    badge: "Hero",
    hero: true,
    art: { base: "#d6c2a8", accent: "#6b4a2c", pattern: "weave" },
    imageCredit:
      "Reference: Cole Haan LunarGrand Wingtip — Milkshake Suede (discontinued) — https://medias.spotern.com/products/w310/24/24323-1571237011.jpg"
  },
  {
    id: "os1-012",
    slug: "letter-crew-sock",
    name: "Letter Crew Sock",
    subtitle: "Cream ribbed cotton, mid-calf",
    category: "accessories",
    price: 24,
    currency: "USD",
    sizes: ["S/M", "L/XL"],
    swatches: [
      { name: "Bone", hex: "#ece1d1" },
      { name: "Driftwood", hex: "#a48064" },
      { name: "Ash", hex: "#c9bdb0" }
    ],
    story:
      "A heavyweight crew that lives above the cuff of the wingtip. Ribbed enough to hold its line on a long walk; soft enough to forget it's there.",
    details: [
      "Long-staple combed cotton, 220 needle",
      "Reinforced toe and heel",
      "Hand-linked toe seam, flat against the foot",
      "Sold in pairs of three"
    ],
    fabric: "Combed cotton",
    origin: "Knit in Northern Portugal",
    art: { base: "#ece1d1", accent: "#a08c75", pattern: "knit" }
  },
  {
    id: "os1-013",
    slug: "os1-earpiece",
    name: "OS1 Earpiece",
    subtitle: "Single bean, magnetic dock",
    category: "tech",
    price: 210,
    currency: "USD",
    sizes: ["One size"],
    swatches: [
      { name: "Bone", hex: "#ece1d1" },
      { name: "Saddle", hex: "#7a4a2c" },
      { name: "Black", hex: "#1a1612" }
    ],
    story:
      "Designed to disappear. A single under-designed bud — closer to a hearing aid than a gadget — that sits in one ear and lets the room come through the other.",
    details: [
      "5.4 g, soft-touch matte shell",
      "Single-ear, neutral-channel speaker",
      "Magnetic charge, no buttons",
      "12 h continuous, 38 h docked"
    ],
    fabric: "Anodized aluminum core, silicone shell",
    origin: "Assembled in Lisbon",
    badge: "Companion",
    art: { base: "#ece1d1", accent: "#7a4a2c", pattern: "satin" },
    imageCredit:
      "Reference: KK Barrett prop earpiece (no SKU, intentionally under-designed) — https://scifiinterfaces.com/wp-content/uploads/2014/02/her-earpiece.jpg"
  },
  {
    id: "os1-014",
    slug: "cameo-book",
    name: "Cameo Book",
    subtitle: "Aluminum-and-leather handheld",
    category: "tech",
    price: 580,
    currency: "USD",
    sizes: ["Pocket"],
    swatches: [
      { name: "Walnut / Saddle", hex: "#6b3a22" },
      { name: "Birch / Bone", hex: "#c9b89a" },
      { name: "Smoked Oak / Black", hex: "#2a1c14" }
    ],
    story:
      "A 1940s address book reissued as a handheld. Brushed aluminum body, leather-embossed inlay, one screen on the verso, a lens on the recto. Designed for the gesture of opening, not the act of unlocking.",
    details: [
      "Anodized aluminum frame, hand-set leather inlay",
      "Single 3.2\" e-ink-hybrid display",
      "Camera flush with the cover, no protrusion",
      "Closes magnetically; opens with a thumb"
    ],
    fabric: "Aluminum + vegetable-tanned leather",
    origin: "Assembled in Lisbon",
    badge: "Limited 200 pieces",
    hero: true,
    art: { base: "#6b3a22", accent: "#2a1408", pattern: "satin" },
    imageCredit:
      "Reference: KK Barrett OS handheld (a.k.a. 'cameo' device) — not a phone, a 1940s address-book form factor — https://scifiinterfaces.com/wp-content/uploads/2014/02/her-cameo.jpg"
  },
  {
    id: "os1-015",
    slug: "cantilever-writing-desk",
    name: "Cantilever Writing Desk",
    subtitle: "Wall-mounted walnut slab, no legs",
    category: "home",
    price: 1240,
    currency: "USD",
    sizes: ["120 cm", "140 cm", "160 cm"],
    swatches: [
      { name: "Walnut", hex: "#5a3a24" },
      { name: "Freijó", hex: "#8a6a48" },
      { name: "Imbuia", hex: "#3a2418" }
    ],
    story:
      "A slab of warm hardwood that holds itself out from the wall on a hidden steel spine. No legs to bump, no skirt to clean — the room reads as one surface.",
    details: [
      "32 mm solid hardwood, chamfered front edge",
      "Concealed 6 mm steel bracket, in-stud or anchor mount",
      "Rated to 90 kg distributed load",
      "Oil-and-wax finish; arrives sealed in linen"
    ],
    fabric: "Solid hardwood + steel spine",
    origin: "Joinery in Salvador",
    badge: "Made to order",
    art: { base: "#5a3a24", accent: "#2a1408", pattern: "weave" },
    imageCredit:
      "Reference: KK Barrett production design — Theodore's apartment desk. Floor plan with KK Barrett's input: https://x.com/FPlanCroissant/status/1902021093184950355"
  },
  {
    id: "os1-016",
    slug: "mantel-monitor-frame",
    name: "Mantel Monitor Frame",
    subtitle: "Walnut surround for a flush LCD",
    category: "home",
    price: 440,
    currency: "USD",
    sizes: ["24\"", "27\"", "32\""],
    swatches: [
      { name: "Walnut", hex: "#5a3a24" },
      { name: "Oak", hex: "#a07852" },
      { name: "Smoked Walnut", hex: "#2c1c12" }
    ],
    story:
      "Turns a thin display into a framed piece of furniture. Mitered walnut, deep enough to swallow the bezel, shallow enough to sit on a writing desk and not block the wall.",
    details: [
      "32 × 38 mm solid walnut, hand-mitered corners",
      "Magnetic back panel, tool-free monitor swap",
      "Fits 24\", 27\", and 32\" panels up to 12 mm thick",
      "Oil finish, brass corner pegs"
    ],
    fabric: "Solid walnut",
    origin: "Joinery in Salvador",
    art: { base: "#5a3a24", accent: "#2a1408", pattern: "weave" },
    imageCredit:
      "Reference: KK Barrett office monitor frames at BeautifulHandwrittenLetters.com. Build log: https://daniel.springwald.de/post/2015/imitating-the-monitor-design-of-the-movie-her"
  }
];

export const collectionGroups = [
  { id: "shirts", label: "Shirts" },
  { id: "outerwear", label: "Outerwear" },
  { id: "knits", label: "Knits" },
  { id: "trousers", label: "Trousers" },
  { id: "footwear", label: "Footwear" },
  { id: "eyewear", label: "Eyewear" },
  { id: "accessories", label: "Accessories" },
  { id: "tech", label: "Tech" },
  { id: "home", label: "Home" }
] as const;

export const formatPrice = (value: number, currency: "USD" = "USD") => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 0
  }).format(value);
};
